






























import Component from 'vue-class-component';
import { ProvideReactive, Watch } from 'vue-property-decorator';
import { AxiosResponse } from 'axios';

// Shared types
import { RequestAddress, RequestClient, RequestOfferRequest } from '@coldtec/cos-types';

// Shared schemas
import { RequestOfferRequestSchema, RequestClientSchema, RequestAddressSchema } from '@coldtec/cos-schemas';

// Store types
import { RequestGetter, RequestMutation } from '@/store/store.types';

// Validation
import Validator, { Errors } from '@/validation';

// The base class for every step
import BaseStep from '../BaseStep.vue';

// Modal
import AlertModal from '@/components/modal/Alert.vue';

// Internal components
import PersonalDetailsData from './internals/PersonalDetailsData.vue';
import PersonalDetailsInstallation from './internals/PersonalDetailsInstallation.vue';
import PersonalDetailsRequestedModal from './internals/PersonalDetailsRequestedModal.vue';
import PersonalDetailsSavedModal from './internals/PersonalDetailsSavedModal.vue';

// Api
import Api from '@/api';

/**
 *  The fifth and final step: filling in personal details and requesting an offer.
 */
@Component({
    components: {
        PersonalDetailsData,
        PersonalDetailsInstallation,
        PersonalDetailsRequestedModal,
        PersonalDetailsSavedModal,
    },
})
export default class PersonalDetailsStep extends BaseStep {

    public $refs!: {
        alert: AlertModal;
        requestedModal: PersonalDetailsRequestedModal;
        savedModal: PersonalDetailsSavedModal;
    };

    /**
     *  Holding the submitted state of the data, starting
     *  real-time validation after submition only.
     */
    private submitted = false;

    /**
     *  Create the validator for the request.
     */
    private validator = new Validator<RequestOfferRequest, RequestClient | RequestAddress>(RequestOfferRequestSchema, [ RequestClientSchema, RequestAddressSchema ]);

    /**
     *  Provide the errors to the data and installation
     *  components so they can be bind their inputs to it.
     */
    @ProvideReactive('errors')
    private errors: Errors<RequestOfferRequest> | null = null;

    /**
     *  Used to get the full request state.
     */
    @ProvideReactive('request')
    private get request(): RequestOfferRequest {

        return this.$store.getters[RequestGetter.REQUEST];

    }

    /**
     *  Construct the errors and update the footer's floating
     *  action and next button accordingly when created.
     */
    private created(): void {

        /**
         *  Construct the errors of the request object.
         */
        this.errors = Validator.constructErrors(this.request);

        /**
         *  Ensure there's no footer floating action and set the
         *  default options of the footer's next button.
         */
        this.setFooterFloatingAction(null);

        this.setFooterNext({
            step: undefined,
            title: 'Vraag de offerte aan',
            content: 'Offerte aanvragen',
            disabled: false,
            action: () => {

                this.submitted = true;

                this.validate();

                if (!this.$store.getters[RequestGetter.VALID]) {

                    return;

                }

                this._submit();

            },
        });

    }

    /**
     *  Invoke the base step's mounted hook when mounted.
     */
    private mounted(): void {

        this.stepMounted();

    }

    /**
     *  Trigger a validation of the request data
     *  on any changes in the request object.
     */
    @Watch('request', { deep: true })
    private validate(): void {

        /**
         *  Ensure the errors are constructed and only validate if submitted.
         */
        if (!this.errors || !this.submitted) {

            return;

        }

        /**
         *  Validate the data with the validator.
         */
        let valid = this.validator.validate(this.request, this.errors);

        /**
         *
         *  Ensure the terms and privacy statements are accepted.
         *
         *  (i) The validator only check if it's a number
         *
         */
        if (valid && (this.request.terms_and_conditions !== 1 || this.request.privacy_statement !== 1)) {

            this.errors.terms_and_conditions = true;
            this.errors.privacy_statement = true;

            valid = false;

        }

        /**
         *  Store the request's new valid state.
         */
        this.$store.commit(RequestMutation.VALID, valid);

        /**
         *  Update the disabled state of the footer's button accordingly.
         */
        this.setFooterNext({ disabled: !valid });

    }

    /**
     *  Used to save the request in the API.
     */
    private async _submit(): Promise<void> {

        this.setFooterNext({ loading: true });

        try {

            /**
             *  Determine the initial HTTP method and uri for saving.
             */
            let method: 'post' | 'put' = 'post';
            let uri = '/requests';

            /**
             *  Update the method and uri accordingly if it should update a request.
             */
            const guid: string | null = this.$store.state.request.guid;

            if (guid) {

                method = 'put';
                uri += `/${ guid }`;

            }

            /**
             *  Execute the request.
             */
            const res: AxiosResponse = await Api.client[method](uri, { request: this.request });

            /**
             *  Show the default modal that is shown when a user requested
             *  a modal if it's about adding a new request.
             */
            if (!guid) {

                this.$refs.requestedModal.open();

                return;

            }

            /**
             *  Forward the returned download location of the offer
             *  to the modal, so the user can download the offer.
             */
            this.$refs.savedModal.open(res.headers.location);

        } catch (_error) {

            void this._showError();

        } finally {

            this.setFooterNext({ loading: false });

        }

    }

    /**
     *  Used to show the error if the API responded
     *  with a non-validation error.
     */
    private async _showError(): Promise<void> {

        void this.$refs.alert.open({
            title: 'Offerte aanvragen mislukt',
            body: 'Helaas ging er iets mis tijdens het verzenden van je aanvraag. Probeer het later opnieuw of klik op de knop hieronder om het meteen opnieuw te proberen.',
            width: '30rem',
        });

    }

}
